import {
  useWSSnackbar,
  WSAlert,
  WSCheckboxToggle,
  WSInfoBox,
  WSList,
  WSModal,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PayeeTaxDocumentSharePermission,
  PayerPayeeStatus
} from "@wingspanhq/payments/dist/interfaces";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import React, { useState } from "react";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useQueryPayerRows } from "../../../query/search/payer/queries/useQueryPayerRows";
import { ErrorContextKey } from "../../../services/platform";
import { useMutationSubmit } from "./useMutationSubmit";

export type FormW9ConsentProps = {
  onClose: () => void;
  payerId?: string;
};

export const W9Consent: React.FC<FormW9ConsentProps> = ({
  onClose,
  payerId,
  ...elementProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  const [shareW9, setShareW9] = useState(false);
  const [submit, submitMeta] = useMutationSubmit({
    onSuccess() {
      openSnackbar({
        type: "success",
        duration: 3000,
        message: `"Consent to share W-9 information" complete`
      });
      onClose();
    }
  });

  const queryPayers = useQueryPayerRows({
    filter: {
      payerId: payerId,
      "payeeOwnedData.status": PayerPayeeStatus.Active,
      "payeeOwnedData.shareTaxDocument": {
        in: [
          PayeeTaxDocumentSharePermission.Decline,
          PayeeTaxDocumentSharePermission.Unspecified
        ]
      }
    },
    page: {
      size: 100
    }
  });

  const payerRows = queryPayers.data || [];
  const payerIds = payerRows.map(row => row.payerId);

  const names = payerRows
    .map(row => {
      const names = wsName({
        user: row.user,
        member: row.member
      });

      return names.getResolvedName();
    })
    .join(", ");

  // The payer search can legitimately come back empty (e.g. the payee side of
  // the relationship is not Active). Submitting in that state would write
  // nothing while reporting success, so consent is not offered at all.
  // A background refetch can fail while an already-loaded list stays usable, so
  // only the states that leave us with nobody to write to block consent.
  const hasRecipients = payerIds.length > 0;
  const hasLoadFailed = queryPayers.isError && !hasRecipients;
  const hasNoRecipients = queryPayers.isSuccess && !hasRecipients;
  const canConsent = !hasLoadFailed && !hasNoRecipients;

  return (
    <WSModal
      size="S"
      title="Share information with client"
      divider
      actions={{
        alignment: "fill",
        buttons: canConsent
          ? [
              {
                label: "Cancel",
                onClick: onClose,
                kind: "Tertiary"
              },
              {
                label: "Continue",
                onClick: () => submit(payerIds),
                kind: "Primary",
                loading: submitMeta.isLoading,
                disabled: !shareW9 || !hasRecipients
              }
            ]
          : [
              {
                label: "Close",
                onClick: onClose,
                kind: "Primary"
              }
            ]
      }}
      onClose={onClose}
    >
      <WSList gap="XL">
        {hasLoadFailed && (
          <WSAlert
            theme="error"
            icon="info-circle"
            name="loadConsentRecipientsFailed"
            title="We couldn't load your client information"
          >
            Your consent was not submitted. Please close this window and try
            again. If the problem continues, contact support.
          </WSAlert>
        )}

        {hasNoRecipients && (
          <WSAlert
            theme="warning"
            icon="info-circle"
            name="noConsentRecipients"
            title="No consent request to complete"
          >
            None of your clients are currently requesting access to your
            taxpayer information. If a client is waiting on this, please contact
            support so we can look into it.
          </WSAlert>
        )}

        {canConsent && (
          <WSInfoBox>
            <WSText.ParagraphSm weight="medium">
              A client is requesting access to your information
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>
              Your client, {names}, is requesting access to your taxpayer
              information or documents in order to fulfill eligibility
              requirements.
            </WSText.ParagraphSm>
          </WSInfoBox>
        )}

        {canConsent && (
          <WSCheckboxToggle
            shimmer={queryPayers.isLoading}
            name="shareW9"
            label={`I consent to share taxpayer information (including documents such as Form W-9 and ID verification) with my client(s) (${names}). I understand this information will be used for compliance and eligibility purposes in accordance with applicable laws and regulations.`}
            value={shareW9}
            onChange={value => {
              setShareW9(value);
            }}
          />
        )}

        <WSErrorMessage
          contextKey={ErrorContextKey.Other}
          error={submitMeta.error}
        />
      </WSList>
    </WSModal>
  );
};
